import type { PuckComponent, PuckData } from "../ai/puck-types.js";
import type { GeneratedVariantSet } from "./variant-generator.js";
import {
  applyVariantOverrides,
  resolveVariant,
  type ComponentVariant,
  type VisitorContext,
} from "./variant-types.js";

export const MAX_DYNAMIC_CONTENT_RULES_PER_PAGE = 20;

export interface ComponentResolution {
  componentIndex: number;
  componentType: string;
  componentId: string;
  variantId: string | null;
  variantLabel: string | null;
  matchedRule: string | null;
}

export interface AddressablePuckComponent {
  componentId: string;
  componentType: string;
  currentProps: Record<string, unknown>;
  source: "content" | "zone";
  zoneId?: string;
}

export interface ResolvedComponentList {
  content: PuckComponent[];
  resolutions: ComponentResolution[];
}

export interface ResolvedDynamicPuckData {
  puckData: PuckData;
  resolutions: ComponentResolution[];
  visitorContext: VisitorContext;
  hasResolutions: boolean;
}

type VariantSetInput = Pick<GeneratedVariantSet, "componentId" | "variants">;

function hasVariantRules(component: PuckComponent): boolean {
  const variants = component.props.variants;
  return Array.isArray(variants) && variants.length > 0;
}

function countVariantRulesOnComponent(component: PuckComponent): number {
  const variants = component.props.variants;
  return Array.isArray(variants) ? variants.length : 0;
}

function mapComponentList(
  components: PuckComponent[] | undefined,
  mapper: (component: PuckComponent) => PuckComponent,
): PuckComponent[] {
  if (!Array.isArray(components)) {
    return [];
  }

  return components.map(mapper);
}

function mergeVariants(
  existingVariants: unknown,
  nextVariants: ComponentVariant[],
): ComponentVariant[] {
  const preserved = Array.isArray(existingVariants)
    ? existingVariants.filter((variant): variant is ComponentVariant => {
        if (!variant || typeof variant !== "object") {
          return false;
        }

        const candidateId = (variant as { id?: unknown }).id;
        return typeof candidateId === "string"
          && !nextVariants.some((nextVariant) => nextVariant.id === candidateId);
      })
    : [];

  return [...preserved, ...nextVariants];
}

export function collectAddressablePuckComponents(
  puckData: Pick<PuckData, "content" | "zones"> | null | undefined,
): AddressablePuckComponent[] {
  const results: AddressablePuckComponent[] = [];

  const collect = (components: PuckComponent[] | undefined, source: "content" | "zone", zoneId?: string) => {
    if (!Array.isArray(components)) {
      return;
    }

    for (const component of components) {
      const componentId = component.props.id;
      if (typeof componentId !== "string" || componentId.length === 0) {
        continue;
      }

      results.push({
        componentId,
        componentType: component.type,
        currentProps: component.props,
        source,
        ...(zoneId ? { zoneId } : {}),
      });
    }
  };

  collect(puckData?.content, "content");

  const zones = puckData?.zones;
  if (zones && typeof zones === "object") {
    for (const [zoneId, components] of Object.entries(zones)) {
      collect(components, "zone", zoneId);
    }
  }

  return results;
}

export function selectAddressablePuckComponents(
  puckData: Pick<PuckData, "content" | "zones"> | null | undefined,
  targetComponentIds: string[],
): AddressablePuckComponent[] {
  const requestedIds = new Set(targetComponentIds);
  return collectAddressablePuckComponents(puckData).filter((component) => requestedIds.has(component.componentId));
}

export function countDynamicContentRules(
  puckData: Pick<PuckData, "content" | "zones"> | null | undefined,
): number {
  const contentCount = Array.isArray(puckData?.content)
    ? puckData.content.reduce((sum, component) => sum + countVariantRulesOnComponent(component), 0)
    : 0;

  const zones = puckData?.zones;
  if (!zones || typeof zones !== "object") {
    return contentCount;
  }

  return Object.values(zones).reduce((sum, components) => {
    if (!Array.isArray(components)) {
      return sum;
    }

    return sum + components.reduce((innerSum, component) => innerSum + countVariantRulesOnComponent(component), 0);
  }, contentCount);
}

export function hasDynamicContentRules(
  puckData: Pick<PuckData, "content" | "zones"> | null | undefined,
): boolean {
  return countDynamicContentRules(puckData) > 0;
}

export function applyGeneratedVariantSets(
  puckData: PuckData,
  sets: VariantSetInput[],
): PuckData {
  const variantsByComponentId = new Map(sets.map((set) => [set.componentId, set.variants]));

  const applyToComponent = (component: PuckComponent): PuckComponent => {
    const componentId = component.props.id;
    if (typeof componentId !== "string") {
      return component;
    }

    const nextVariants = variantsByComponentId.get(componentId);
    if (!nextVariants) {
      return component;
    }

    return {
      ...component,
      props: {
        ...component.props,
        variants: mergeVariants(component.props.variants, nextVariants),
      },
    };
  };

  const nextZones = puckData.zones
    ? Object.fromEntries(
      Object.entries(puckData.zones).map(([zoneId, components]) => [
        zoneId,
        mapComponentList(components, applyToComponent),
      ]),
    )
    : undefined;

  return {
    ...puckData,
    content: mapComponentList(puckData.content, applyToComponent),
    ...(nextZones ? { zones: nextZones } : {}),
  };
}

export function resolveComponentList(
  components: PuckComponent[],
  ctx: VisitorContext,
): ResolvedComponentList {
  const resolvedContent: PuckComponent[] = [];
  const resolutions: ComponentResolution[] = [];

  for (let i = 0; i < components.length; i++) {
    const component = components[i]!;
    const variants = component.props.variants as ComponentVariant[] | undefined;

    if (!Array.isArray(variants) || variants.length === 0) {
      resolvedContent.push(component);
      resolutions.push({
        componentIndex: i,
        componentType: component.type,
        componentId: component._id ?? `${component.type}-${i}`,
        variantId: null,
        variantLabel: null,
        matchedRule: null,
      });
      continue;
    }

    const matched = resolveVariant(variants, ctx);
    const resolvedProps = applyVariantOverrides(component.props, matched);
    const { variants: _variants, ...cleanProps } = resolvedProps as Record<string, unknown> & { variants?: unknown };

    resolvedContent.push({
      type: component.type,
      props: cleanProps,
      _id: component._id,
    });

    resolutions.push({
      componentIndex: i,
      componentType: component.type,
      componentId: component._id ?? `${component.type}-${i}`,
      variantId: matched?.id ?? null,
      variantLabel: matched?.label ?? null,
      matchedRule: matched ? `${matched.rule.type}:${matched.rule.value}` : null,
    });
  }

  return { content: resolvedContent, resolutions };
}

export function resolveDynamicPuckData(
  puckData: PuckData,
  ctx: VisitorContext,
): ResolvedDynamicPuckData {
  const base = resolveComponentList(puckData.content, ctx);
  const zones = puckData.zones ?? {};
  const nextZones: Record<string, PuckComponent[]> = {};
  const allResolutions = [...base.resolutions];

  for (const [zoneId, components] of Object.entries(zones)) {
    const resolvedZone = resolveComponentList(components, ctx);
    nextZones[zoneId] = resolvedZone.content;
    allResolutions.push(...resolvedZone.resolutions);
  }

  return {
    puckData: {
      ...puckData,
      content: base.content,
      ...(Object.keys(nextZones).length > 0 ? { zones: nextZones } : {}),
    },
    resolutions: allResolutions,
    visitorContext: ctx,
    hasResolutions: allResolutions.some((resolution) => resolution.variantId !== null),
  };
}

export function hasVariants(components: PuckComponent[]): boolean {
  return components.some(hasVariantRules);
}
